import { Doc } from '@dadohr/shared/domainTypes/document';
import { IExperienceTemplate } from '@dadohr/shared/domainTypes/experience';
import { FS } from '@dadohr/shared/versions';
import {
  toExperienceDoc,
  toExperienceTemplateDoc
} from '../../../services/experiences/documents';
import {
  getDuplicateEnrollmentForm,
  getEnrollmentFormByTemplate
} from '../../../services/experiences/enrollmentForms';
import { getDuplicate } from '../../../services/experiences/template';
import { setDoc, store } from '../../../services/firebase';
import {
  CollectionListener,
  createCollectionListenerStore,
  createSingleCollectionListenerStore,
  useCollectionListener
} from '../../../services/firecache/collectionListener';
import { checkAndReportUndefined, sanitizeUndefined } from '../../../services/object';

const experienceTemplateCollection = () =>
  store().collection(FS.experienceTemplates);

const experienceCollection = () => store().collection(FS.experiences);

const getExperienceTemplatesListener = createSingleCollectionListenerStore(
  () =>
    new CollectionListener(
      experienceTemplateCollection(),
      toExperienceTemplateDoc
    )
);

const getExperienceTemplatesListenerByOrgId = createCollectionListenerStore(
  (orgId) =>
    new CollectionListener(
      experienceTemplateCollection().where('orgId', '==', orgId),
      toExperienceTemplateDoc
    )
);

const getExperiencesListenerByTemplateId = createCollectionListenerStore(
  (templateId) =>
    new CollectionListener(
      experienceCollection().where('templateId', '==', templateId),
      toExperienceDoc
    )
);

const getExperienceInstancesListenerByOrgId = createCollectionListenerStore(
  (orgId) =>
    new CollectionListener(
      experienceCollection().where('orgId', '==', orgId),
      toExperienceDoc
    )
);

const getAllExperiencesListener = createSingleCollectionListenerStore(
  () => new CollectionListener(experienceCollection(), toExperienceDoc)
);

export const useAllExperienceTemplates = () => {
  return useCollectionListener(getExperienceTemplatesListener());
};

export const getAllExperienceTemplates = () => {
  return getExperienceTemplatesListener().get();
};

export const useAllExperiences = () => {
  return useCollectionListener(getAllExperiencesListener());
};

export const getAllExperiences = () => {
  return getAllExperiencesListener().get();
};

export const getExperienceIntancesByTemplateId = (templateId: string) => {
  return getExperiencesListenerByTemplateId(templateId).get();
};

export const useExperienceInstancesByTemplateId = (templateId: string) => {
  return useCollectionListener(getExperiencesListenerByTemplateId(templateId));
};

export const getAllExperienceInstancesByOrgId = (orgId: string) => {
  return getExperienceInstancesListenerByOrgId(orgId).get();
};

export const getAllExperienceTemplatesByOrgId = (orgId: string) => {
  return getExperienceTemplatesListenerByOrgId(orgId).get();
};

export const copyExperienceTemplateToOtherOrg = async (
  tpl: Doc<IExperienceTemplate>,
  nextOrg: string,
  tz: string,
  fixUndefined: boolean = false
) => {
  const duplicate = getDuplicate(tpl.data, tpl.data.createdBy, true);
  if (!fixUndefined) {
    checkAndReportUndefined(duplicate, 'Experience template');
  } else {
    sanitizeUndefined(duplicate);
  }
  

  const enrollmentForm = await getEnrollmentFormByTemplate(
    tpl.data.orgId,
    tpl.id
  );
  if (enrollmentForm) {
    const duplicatedEnrollmentForm = getDuplicateEnrollmentForm(
      enrollmentForm.data,
      enrollmentForm.data.modifiedBy,
      duplicate.id
    );
    duplicatedEnrollmentForm.data.orgId = nextOrg;
    if (!fixUndefined) {
      checkAndReportUndefined(duplicatedEnrollmentForm, 'Enrollment form');
    } else {
      sanitizeUndefined(duplicatedEnrollmentForm);
    }
    
    await setDoc(duplicatedEnrollmentForm);
  }
  duplicate.data.orgId = nextOrg;
  duplicate.data.tz = tz;
  return setDoc(duplicate);
};
